import React, { useCallback, useEffect, useRef, useState } from 'react';
import { transcribeApi } from '../api/transcribe.api';

interface VoiceRecorderProps {
  // Called with the transcribed text once a recording finishes uploading.
  onTranscribed: (text: string) => void;
  disabled?: boolean;
}

type RecorderStatus = 'idle' | 'requesting-permission' | 'recording' | 'transcribing' | 'error';

const MAX_RECORDING_SECONDS = 120;

const CANDIDATE_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
  'audio/ogg',
];

function pickSupportedMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return undefined;
  }
  return CANDIDATE_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
}

/**
 * File extension matching the container the recorder actually produced.
 *
 * The transcription API decides how to decode the upload from this
 * extension, so it has to be right: Ogg is in the candidate list above (some
 * Android browsers support neither WebM nor MP4), and previously any
 * non-MP4 recording was labelled ".webm" regardless — sending Ogg bytes
 * under a WebM name.
 */
function extensionFor(mimeType: string | undefined): string {
  const container = (mimeType || 'audio/webm').split(';')[0].trim().toLowerCase();
  if (container.includes('mp4') || container.includes('m4a') || container.includes('aac')) return 'm4a';
  if (container.includes('ogg')) return 'ogg';
  if (container.includes('wav')) return 'wav';
  if (container.includes('mpeg') || container.includes('mp3')) return 'mp3';
  return 'webm';
}

function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

const isVoiceRecordingSupported =
  typeof navigator !== 'undefined' &&
  !!navigator.mediaDevices?.getUserMedia &&
  typeof MediaRecorder !== 'undefined';

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({ onTranscribed, disabled }) => {
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [error, setError] = useState('');
  const [detectedLanguage, setDetectedLanguage] = useState<string | null>(null);
  const [wasTranslated, setWasTranslated] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      stopTimer();
      releaseStream();
    };
  }, [stopTimer, releaseStream]);

  const finishRecording = useCallback(() => {
    mediaRecorderRef.current?.stop();
  }, []);

  const startRecording = useCallback(async () => {
    setError('');
    setDetectedLanguage(null);
    setWasTranslated(false);
    setStatus('requesting-permission');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = pickSupportedMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        stopTimer();
        releaseStream();

        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        chunksRef.current = [];

        if (blob.size === 0) {
          setStatus('error');
          setError('No audio was captured. Please try again.');
          return;
        }

        setStatus('transcribing');
        try {
          const result = await transcribeApi.transcribeAudio(blob, `voice-prompt.${extensionFor(recorder.mimeType)}`);
          setDetectedLanguage(result.language || null);
          // The AI that builds the website reads English prompts more
          // reliably, so prefer the translated text whenever the speech
          // wasn't already in English — the user can still edit it below.
          setWasTranslated(!!result.translatedText);
          onTranscribed(result.translatedText || result.text);
          setStatus('idle');
        } catch (err: any) {
          setStatus('error');
          setError(err.response?.data?.message || 'Failed to transcribe your recording. Please try again or type instead.');
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setStatus('recording');
      setElapsedSeconds(0);

      timerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => {
          const next = prev + 1;
          if (next >= MAX_RECORDING_SECONDS) {
            finishRecording();
          }
          return next;
        });
      }, 1000);
    } catch (err: any) {
      setStatus('error');
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        setError('Microphone access was denied. Please allow microphone access and try again.');
      } else if (err?.name === 'NotFoundError') {
        setError('No microphone was found on this device.');
      } else {
        setError('Could not start recording. Please try again or type your description instead.');
      }
    }
  }, [finishRecording, releaseStream, stopTimer, onTranscribed]);

  if (!isVoiceRecordingSupported) {
    return null;
  }

  const isRecording = status === 'recording';
  const isTranscribing = status === 'transcribing';
  const isBusy = status === 'requesting-permission' || isRecording || isTranscribing;

  return (
    <div className="space-y-2">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <button
          type="button"
          onClick={isRecording ? finishRecording : startRecording}
          disabled={disabled || status === 'requesting-permission' || isTranscribing}
          className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed self-start ${
            isRecording
              ? 'bg-nova-red/15 text-nova-red border border-nova-red/40 hover:bg-nova-red/25'
              : 'bg-nova-sky/10 text-nova-sky border border-nova-sky/30 hover:bg-nova-sky/20'
          }`}
        >
          {isRecording ? (
            <>
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-nova-red opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-nova-red" />
              </span>
              Stop recording ({formatTime(elapsedSeconds)})
            </>
          ) : isTranscribing ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Transcribing...
            </>
          ) : status === 'requesting-permission' ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Requesting mic access...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
              </svg>
              Record your description
            </>
          )}
        </button>

        {!isBusy && (
          <span className="text-xs text-nova-text3">Speak in English or Hindi — up to 2 minutes</span>
        )}
      </div>

      {isRecording && (
        <div className="w-full h-1 bg-nova-bg3 rounded-full overflow-hidden">
          <div
            className="h-full bg-nova-red transition-all duration-1000 ease-linear"
            style={{ width: `${Math.min((elapsedSeconds / MAX_RECORDING_SECONDS) * 100, 100)}%` }}
          />
        </div>
      )}

      {status === 'error' && error && (
        <p className="text-sm text-nova-red">{error}</p>
      )}

      {status === 'idle' && detectedLanguage && (
        <p className="text-xs text-nova-green">
          {wasTranslated
            ? `Detected ${detectedLanguage.charAt(0).toUpperCase() + detectedLanguage.slice(1)} speech — translated to English below. Feel free to edit it.`
            : `Transcribed from ${detectedLanguage.charAt(0).toUpperCase() + detectedLanguage.slice(1)} speech — feel free to edit the text below.`}
        </p>
      )}
    </div>
  );
};

export default VoiceRecorder;
